<template>
  <div id="CatalogListing" class="container">
    <b-row v-if="CatalogIsLoading" class="justify-content-center">
      <loading-spinner />
    </b-row>
    <b-row v-else>
      <b-col sm="12" lg="3">
        <template
          v-if="Object.keys(selectedFacets).length === 0 && Object.keys(categoryFacets).length === 0"
        >
          <b-row class="catalog-listing_side_menu left-menu">
            <CategoriesSideMenu :categories="CatalogRoot" />
          </b-row>
          <br>
        </template>
        <b-row
          v-if="Object.keys(selectedFacets).length > 0 || Object.keys(categoryFacets).length > 0"
          class="catalog-listing_side_menu left-menu"
        >
          <part-search-facets
            :facets="categoryFacets"
            :selected-facets="selectedFacets"
            @facets-updated="updateFacets"
          />
        </b-row>
        <br>
        <b-row
          v-if="noRelatedDocuments === false"
          class="catalog-listing_side_menu left-menu"
        >
          <related-documents-menu
            :category-id="currentCategoryId"
            @foundNoRelatedDocuments="setNoRelatedDocuments()"
          />
        </b-row>
      </b-col>
      <b-col sm="12" lg="9">
        <b-row v-if="CategoryIsLoading && currentCategoryId > 0 " class="justify-content-center">
          <loading-spinner />
          <hr>
        </b-row>
        <b-row v-else>
          <b-col v-if="hasCurrentCategory">
            <b-row class="no-gutters category-listing-header">
              <b-col class="col-12 col-md-7 mx-0">
                <h1>{{ CurrentCategory.Title }}</h1>
              </b-col>
            </b-row>
            <b-row v-if="IsCollapseButtonVisible">
              <b-col>
                <dn-function-button
                  :text="viewButtonText"
                  :button-id="'switchToGridView'"
                  :callback-fn="switchViews"
                  class="category-part-search__view-button"
                /> 
                <dn-function-button
                  v-if="!gridView"
                  :text="CollapseDescriptionText"
                  :button-id="'collapseAllDescriptionsButton'"
                  :callback-fn="toggleCollapseAllDescriptions"
                  :size="mediumSize"
                /> 
              </b-col>
            </b-row>
            <b-row v-if="noContentHasLoaded === true" class="justify-content-center">
              <loading-spinner />
            </b-row>
            <b-row class="mx-1">
              <marketing-text
                :category-id="currentCategoryId"
                :disable-loading-spinner="true"
                :is-collapsible="CurrentCategory.IsCollapsibleMarketing"
                :marketing-title="CurrentCategory.MarketingTitle"
                @loaded="setContentLoaded()"
              />
            </b-row>
            <b-row v-if="hasChildren" class="mx-1">
              <category-children
                :parent-category-id="currentCategoryId"
                :static-children="CurrentCategory.ChildCategories"
                @loaded="setContentLoaded()"
              />
            </b-row>
            <div>
              <category-part-search
                ref="categoryPartSearch"
                :part-search-model="partSearchModel"
                :disable-loading-spinner="false"
                :collapse-part-description="collapseAllPartDescriptionsValue"
                :grid-view="gridView"
                :parts-per-page="partsPerPage"
                @partSearch="getCategoryParts"
                @loaded="setContentLoaded()"
                @resultsPerPageChanged="setResultsPerPage"
                @sortTermChanged="setSortTerm"
              />
            </div>
            <b-row class="mx-1">
              <kits
                :category-id="currentCategoryId"
                :disable-loading-spinner="true"
                @loaded="setContentLoaded()"
              />
            </b-row>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </div>
</template>

<script>  
  import { mapGetters } from "vuex";
  import { constants } from "@/const";

  // mixins
  import { 
    siteSectionsHelper,
    sizeHelper 
  } from "@/components/mixins";

  // elements
  import { 
    DnFunctionButton,
    LoadingSpinner
  }         from "@/components/Elements";

  // services
  import { CatalogService } from "@/services/CatalogService";
  import { ErrorLogService } from "@/services/ErrorLogService";
  import { PartsService } from "@/services/PartsService";
  import { UserSettingsService }    from "@/services";

  // submenus
  import CategoriesSideMenu from "@/components/SubMenus/CategoriesSideMenu";
  import RelatedDocumentsMenu from "@/components/SubMenus/RelatedDocumentsMenu";
  import PartSearchFacets from "@/components/SubMenus/PartSearchFacets";

  // catalog components
  import MarketingText from "@/components/Catalog/MarketingText";
  import CategoryChildren from "@/components/Catalog/CategoryChildren";
  import { default as CategoryPartSearch } from "@/components/Catalog/CategoryPartSearch";
  import Kits from "@/components/Catalog/Kits";

  export default {
    components: {
      CategoriesSideMenu,
      MarketingText,
      CategoryChildren,
      CategoryPartSearch,
      PartSearchFacets,
      RelatedDocumentsMenu,
      Kits,
      LoadingSpinner,
      DnFunctionButton
    },
    mixins: [ siteSectionsHelper, sizeHelper ],
    props: {
      currentCategoryId: {
        type: String,
        default: ""
      },
    },
    data: () => ({
      isSelectedCategoryLoading: true,
      partSearchModel: {
        SearchValue: String,
        CategoryParts: Array,
        GridCategoryParts: Array,
        Facets: Object,
        NumberOfPages: Number,
        NumberOfResults: Number,
        CurrentPageNumber: 1,
      },
      categoryFacets: {},
      keywordSearch: "",
      numberOfParts: 0,
      selectedFacets: {},
      noRelatedDocuments: false,
      noContentHasLoaded: true,
      SearchId: String,
      IsCollapseButtonVisible: false,
      gridCols: 100,
      gridView: false,
      partsPerPage: 10,
      partsPerPageChanged: false,
      sortOption: null,
    }),
    computed: {
      ...mapGetters([
        "user",
        "activeAccountId",
        "CatalogRoot",
        "CatalogIsLoading",
        "CurrentCategory",
        "CategoryIsLoading",
        "audienceAndExclusions",
        "facingWarehouse",
        "audienceIds",
        "getRecentComponentName",
        "getRecentComponentData",
        "collapseAllPartDescriptions",
        "collapseAllPartDescriptionsValue",
        "gridViewDefaultValue" 
      ]),
      hasCurrentCategory() {
        return this.IsNotNullOrUndefined(this, "CurrentCategory");
      },
      hasChildren() {
        return (
          this.IsNotNullOrUndefined(this, "CurrentCategory.ChildCategories") &&
          this.CurrentCategory.ChildCategories.length > 0
        );
      },
      CollapseDescriptionText(){        
        return this.collapseAllPartDescriptionsValue ? "Expand All Descriptions" : "Collapse All Descriptions";
      },
      viewButtonText(){
        return this.gridView ? "Switch To List View" : "Switch To Grid View";
      },
    },
    created() {

      this.gridView = this.gridViewDefaultValue === "true" || this.gridViewDefaultValue === true

      var queryFacets = this.$route.query.facets;

      if(queryFacets){
        this.selectedFacets = JSON.parse(queryFacets);
      }
      else if (this.getRecentComponentName === `Catalog_Facets_${this.currentCategoryId}`) {
        this.selectedFacets = this.getRecentComponentData;
      }

      this.$store.dispatch("SET_CURRENT_CATEGORY_ID", this.currentCategoryId);
      if (this.CurrentCategory === undefined) {
        this.$store.dispatch("LOAD_CATEGORY", {
          accountId: this.activeAccountId,
          userId: this.user.UserId,
          categoryId: this.currentCategoryId,
          audienceIds: this.audienceIds,
        });
      }
      


    },
    methods: {
      updateFacets() {
        this.$store.commit("SET_RECENT_COMPONENT", {
          name: `Catalog_Facets_${this.currentCategoryId}`,
          data: this.selectedFacets,
        });
  
        var sortKey = this.IsNotNullOrUndefined(this.sortOption, "sortKey") ? this.sortOption.sortKey : null;
        var sortDirection = this.IsNotNullOrUndefined(this.sortOption, "sortDirection") ? this.sortOption.sortDirection : null;

        this.getCategoryParts(true,sortKey, sortDirection);
        var facetsString = JSON.stringify(this.selectedFacets);

        var url = new URL(window.location.href);
        url.searchParams.set('currentCategoryId', this.currentCategoryId);
        url.searchParams.set('facets', facetsString);
        window.history.pushState(null, '', url.toString());
      },
      getCategoryParts(isAddingFacet, sortTerm, sortDirection, pageSize, inStockFilter) {
        let self = this;
        this.toggleFacetsEnabled(false);
        if (isAddingFacet || this.partsPerPageChanged) {
          self.partSearchModel.CurrentPageNumber = 1;
        }

        CatalogService.GetCategoryParts(
          self.activeAccountId,
          self.user.UserId,
          self.CurrentCategory.Id,
          self.partSearchModel.CurrentPageNumber,
          self.selectedFacets,
          self.CurrentCategory.Title,
          self.SearchId,
          sortTerm,
          sortDirection,
          this.partsPerPage,
          inStockFilter 
        )
          .then((searchResults) => {
            Object.assign(self.partSearchModel, searchResults.data);

            delete self.partSearchModel.Facets;
            delete searchResults.data.Facets.Facets.Category;
            self.categoryFacets = searchResults.data.Facets.Facets;
            self.SearchId = searchResults.data.SearchId;
            let audienceIds = self.audienceAndExclusions ? self.audienceAndExclusions.AudienceIds : null;
            self.numberOfParts = searchResults.data.NumberOfResults;
            var categoryParts = self.partSearchModel.CategoryParts;
            
            self.partSearchModel.CategoryParts.forEach(function(part, index, parts) {
              //Abandon search if its not the latest
              if (searchResults.data.SearchId !== self.SearchId){
                return;
              }
              PartsService.FullPartDetails(self.activeAccountId, self.user.UserId, part.Id, audienceIds)
                .then((fullPartDetail) => {
                  //Adding to temp var to avoid errors in case self.partsearchmodel changes in the middle of the search
                  self.$set(categoryParts[index], "FullPartDetails", fullPartDetail.data);
                })
                .catch((err) => {
                  ErrorLogService.LogException(err);
                });
            });
            self.partSearchModel.CategoryParts = categoryParts;

            if(self.numberOfParts > 0){
              this.IsCollapseButtonVisible = true;
            }
            self.createGridStructure(self.partSearchModel.CategoryParts);
          })
          .catch((err) => {
            ErrorLogService.LogException(err);
          })
          .finally(() => {
            if (this.$refs.categoryPartSearch) {
              this.$refs.categoryPartSearch.isLoadingParts = false;
              this.$refs.categoryPartSearch.isLoadingNextPageParts = false;
            }
            this.toggleFacetsEnabled(true);
            this.partsPerPageChanged = false
          });
      },
      createGridStructure(){
        var gridParts = [];
        for(var i = 0; i < this.partSearchModel.CategoryParts.length/this.gridCols;i++){
          var start = i *this.gridCols;
          var end = start + this.gridCols;
          gridParts.push(this.partSearchModel.CategoryParts.slice(start,end));
        }
        this.partSearchModel.GridCategoryParts = gridParts;
      },
      async switchViews(){
        // switch views and save value to store
        this.gridView = !this.gridView;
        this.$store.commit("SET_GRID_VIEW_DEFAULT", this.gridView);
        this.$refs.categoryPartSearch.performPartSearch();        

        // get, update, and save the user settings in the DB as well
        await UserSettingsService.GetUserSettings(this.user.UserId)
          .then( userSettings => {
            if(this.IsNotNullOrUndefined(userSettings) && userSettings !== ""){
              this.$store.commit("SET_USER_SETTINGS", userSettings)

              let gridViewUserSetting = userSettings.filter(setting => setting.SettingName === "Grid View Default");
              if( gridViewUserSetting.length > 0){
                gridViewUserSetting[0].SettingValue = this.gridView;
                UserSettingsService.SaveUserSettings(gridViewUserSetting);
              }
              else{
                this.saveNewGridViewUserSetting();
              }
            }
            else{
              this.saveNewGridViewUserSetting();
            }
          });
      },
      saveNewGridViewUserSetting(){
        let newGridViewUserSetting = [{
          SettingName: "Grid View Default",
          SettingValue: this.gridView,
          UserId: this.user.UserId
        }]
        this.$store.commit("ADD_USER_SETTINGS", newGridViewUserSetting[0]);
        UserSettingsService.SaveUserSettings(newGridViewUserSetting);
      },
      toggleFacetsEnabled(isEnabled) {
        let facetItems = document.getElementsByClassName("category-facet");
        [...facetItems].forEach(function(element) {
          if (isEnabled === false && element !== undefined && element.className !== undefined) {
            element.className += " catalog-listing_disabled_facet_list_item";
          } else if (isEnabled === true) {
            element.className = "category-facet";
          }
        });
      },
      setContentLoaded() {
        this.noContentHasLoaded = false;
      },
      setNoRelatedDocuments() {
        this.noRelatedDocuments = true;
      },
      toggleCollapseAllDescriptions(){
        this.$store.commit("SET_COLLAPSE_ALL_PART_DESCRIPTIONS", !this.collapseAllPartDescriptionsValue);
      },
      setResultsPerPage(resultsPerPage){
        this.partsPerPage = resultsPerPage;
        this.partsPerPageChanged = true;
      },
      setSortTerm(sortOption){
        this.sortOption = sortOption;
      }
    },
  };
</script>
<style lang="scss">
//nested styles intentional to increase specificity
.category-listing-header {
  flex-direction: row;
  .catalog-search-input {
    width: 80%;

    @media (min-width: $screen-sm-min) {
      margin-left: 58px;
    }
  }
  h1 {
    font-size: 30px;
  }
}
.catalog-listing_side_menu {
  .nav-sub,
  .related-documents-menu,
  #Categories {
    @media (max-width: $screen-md-max) {
      width: 100%;
    }
  }
}

.catalog-listing_disabled_facet_list_item {
  pointer-events: none;
  opacity: 0.5;
}
</style>